import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import './TableContainer.css';
import './PieChartContainer.css'; // Importa el nuevo archivo CSS
import Table from './Table';
import BarraContainer from './BarraContainer';
import exportToExcel from './exportToExcel';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, Tooltip } from 'recharts';


const TableContainer = ({ title, data, columns, onRowClick, onRowDoubleClick, handleButtonClick, loading, error, className, buttonList, navigationHistory, showPieChart }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState(columns[0]?.field || '');
  const [showChart, setShowChart] = useState(false);

  const itemsPerPage = 30;

  const dataArray = Array.isArray(data) ? data : [];

  const filteredData = useMemo(() => {
    return dataArray.filter((item) => {
      if (!filterField || !searchTerm) return true;
      return item[filterField]?.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [dataArray, filterField, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [currentPage, filteredData]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
  };

  const visibleColumns = columns.filter(col => col.visible !== false);

  const handleButtonClickIn = (boton) => {
    if (boton === 'export'){
      exportToExcel(title, filteredData, columns);
    } else {
      handleButtonClick(boton);
    }
  };

  const pieChartData = useMemo(() => {
    const totalDataCount = dataArray.length;
    const countByField = filteredData.reduce((acc, item) => {
      const key = item[filterField];
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key]++;
      return acc;
    }, {});

    const filteredCount = Object.values(countByField).reduce((sum, value) => sum + value, 0);
    const otherCount = totalDataCount - filteredCount;

    const pieData = Object.entries(countByField).map(([key, value]) => ({
      name: key,
      value,
    }));

    if (otherCount > 0) {
      pieData.push({ name: 'Otros', value: otherCount });
    }
    return pieData;
  }, [dataArray, filteredData, filterField]);



// Convert hex color to RGB
const hexToRgb = (hex) => {
  const bigint = parseInt(hex.slice(1), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
};

// Convert RGB to hex color
const rgbToHex = (r, g, b) => {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
};

// Function to generate variations of a pastel color
const generatePastelVariations = (color, totalVariations) => {
  const baseRgb = hexToRgb(color);
  const variations = [];

  for (let i = 0; i < totalVariations; i++) {
    // Ajustar los valores RGB para generar colores pasteles con suficiente contraste
    const variation = {
      r: Math.min(255, Math.max(0, baseRgb.r + Math.floor((i *  Math.random()) * 20) % 150)),
      g: Math.min(255, Math.max(0, baseRgb.g + Math.floor((i *  Math.random()) * 20) % 150)),
      b: Math.min(255, Math.max(0, baseRgb.b + Math.floor((i *  Math.random()) * 20) % 150))
    }
    variations.push(rgbToHex(variation.r, variation.g, variation.b));
  }
  
  return variations;
};

// Function to shuffle an array
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// Function to generate pastel colors with sufficient contrast
const generatePastelColors = (baseColors) => {
  const colors = new Set(); // Utilizamos un Set para evitar duplicados
  
  baseColors.forEach(color => {
    const colorVariations = generatePastelVariations(color, 10);
    colorVariations.forEach(variation => colors.add(variation)); // Añadimos cada variación al Set
  });
  
  return Array.from(colors); // Convertimos el Set de nuevo a un arreglo
};

// Base colors provided
const baseColors = [
  '#3B3B6D', // Dark Blue
  '#6D3B3B', // Dark Red
  '#3B6D6D', // Dark Teal
  '#3B6D4A', // Dark Green
  '#6D4A3B', // Dark Brown
  '#4A3B6D', // Dark Purple
  '#6D3B5A', // Dark Magenta
  '#5A6D3B', // Dark Olive
  '#6D5A3B', // Dark Golden
  '#3B4A6D', // Dark Steel Blue
  '#4A6D3B', // Dark Lime Green
  '#6D3B4A', // Dark Maroon
  '#3B6D5A', // Dark Sea Green
  '#6D5A6D', // Dark Orchid
  '#5A3B6D'  // Dark Slate Blue
];


// Generate pastel colors with sufficient contrast
const COLORS = shuffleArray(shuffleArray(generatePastelColors(shuffleArray(baseColors))));

const effectiveShowPieChart = useMemo(() => {
  return dataArray.length > 0 && showPieChart;
}, [dataArray.length, showPieChart]);


  return (
    <div className={className}>
      <h2>{title}</h2>
      {effectiveShowPieChart & showChart ? (
        <div className="chart-container">
          <PieChart width={400} height={400} className="pie-chart">
            <Pie
              data={pieChartData}
              cx={200}
              cy={200}
              labelLine={false}
              label={({ name, value }) => `${name}: ${value}`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--text-color)' }} />
          </PieChart>
          <BarChart
            width={500}
            height={400}
            data={pieChartData}
            margin={{
              top: 5, right: 30, left: 20, bottom: 5,
            }}
            className="bar-chart"
          >
            <CartesianGrid strokeDasharray="1 1" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip contentStyle={{ backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--text-color)         ' }} />
            <Bar name = 'valor' dataKey="value" fill="#8884d8">
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </div>
      ) : (
        <></>
      )}
      {effectiveShowPieChart ? (
        <BarraContainer 
          buttonList={buttonList} 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterField={filterField}
          setFilterField={setFilterField}
          columns={visibleColumns}
          handleButtonClick={handleButtonClickIn}
          navigationHistory={navigationHistory}
          showChart ={showChart}
          setShowChart ={setShowChart}
        />
      ):(
        <BarraContainer 
          buttonList={buttonList} 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterField={filterField}
          setFilterField={setFilterField}
          columns={visibleColumns}
          handleButtonClick={handleButtonClickIn}
          navigationHistory={navigationHistory}
        />
      )}
      <div className="table-container">
        {loading && <p>Cargando...</p>}
        {error && <p>{error}</p>}
        {dataArray.length === 0 ? (
          <p>No hay datos disponibles</p>
        ) : (
          <>
            <Table data={paginatedData} columns={visibleColumns} onRowClick={onRowClick} onRowDoubleClick={onRowDoubleClick} />
            <div className="pagination">
              <button className="pagination-button" onClick={handlePrevPage} disabled={currentPage === 1}>
                Anterior
              </button>
              <span>{currentPage} de {totalPages}</span>
              <button className="pagination-button" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

TableContainer.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    visible: PropTypes.bool
  })).isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  handleButtonClick: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  buttonList: PropTypes.arrayOf(PropTypes.string).isRequired,
  navigationHistory: PropTypes.arrayOf(PropTypes.string).isRequired,
  showPieChart: PropTypes.bool // Nueva propiedad para mostrar o no el gráfico de torta
};

TableContainer.defaultProps = {
  loading: false,
  error: null,
  className: '',
  buttonList: [],
  showPieChart: false // Valor por defecto es falso
};

export default TableContainer;
